import { CheckCircle, Circle, AlertCircle, Twitter, Shield, Star, User } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import type { BroUser } from './types';
import type { JobBoardAccess } from '@/hooks/useJobBoardAccess';

interface VerificationStep {
  id: string;
  title: string;
  description: string;
  action: string;
  completed: boolean;
}

interface VerificationStatusProps {
  user: BroUser;
  access: JobBoardAccess;
  verificationSteps: VerificationStep[];
}

const VerificationStatus: React.FC<VerificationStatusProps> = ({
  user,
  access
}) => {
  const getStepIcon = (stepId: string, completed: boolean) => {
    const iconProps = { size: 20, className: completed ? 'text-green-400' : 'text-tan-dark' };
    
    switch (stepId) {
      case 'brocode':
        return <User {...iconProps} />;
      case 'twitter':
        return <Twitter {...iconProps} />;
      case 'telegram':
        return <Shield {...iconProps} />;
      case 'broscore':
        return <Star {...iconProps} />;
      case 'escrow':
        return <Shield {...iconProps} />;
      default:
        return completed ? <CheckCircle {...iconProps} /> : <Circle {...iconProps} />;
    }
  };

  const getCompletionPercentage = () => {
    const totalSteps = 4; // BroCode, Twitter, Telegram, BroScore
    let completedSteps = 0;
    
    if (access.hasValidBroCode) completedSteps++;
    if (access.hasTwitterHandle) completedSteps++;
    if (access.isVerified) completedSteps++;
    if (access.hasMinimumScore) completedSteps++;
    
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const getVerificationLevelInfo = () => {
    switch (access.verificationLevel) {
      case 'none':
        return {
          title: 'Getting Started',
          description: 'Complete verification to access job board features',
          color: 'bg-red-500',
          textColor: 'text-red-400'
        };
      case 'browse':
        return {
          title: 'Browse Level',
          description: 'You can view jobs but need verification to apply',
          color: 'bg-yellow-500',
          textColor: 'text-yellow-400'
        };
      case 'apply':
        return {
          title: 'Apply Level',
          description: 'You can apply to jobs! Increase BroScore to post jobs',
          color: 'bg-blue-500',
          textColor: 'text-blue-400'
        };
      case 'post':
      case 'full':
        return {
          title: 'Full Access',
          description: 'You have complete access to all job board features',
          color: 'bg-green-500',
          textColor: 'text-green-400'
        };
      default:
        return {
          title: 'Unknown',
          description: 'Verification status unknown',
          color: 'bg-gray-500',
          textColor: 'text-gray-400'
        };
    }
  };

  const levelInfo = getVerificationLevelInfo();
  const completionPercentage = getCompletionPercentage();

  return (
    <Card className="bg-navy-light border-tan-dark">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-tan text-lg">Verification Status</CardTitle>
            <CardDescription className="text-tan-dark">
              Track your progress to unlock job board features
            </CardDescription>
          </div>
          <Badge className={`${levelInfo.color} text-white`}>
            {levelInfo.title}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-tan">Verification Progress</span>
            <span className="text-tan-dark">{completionPercentage}%</span>
          </div>
          <Progress 
            value={completionPercentage} 
            className="h-2 bg-navy border-tan-dark"
          />
          <p className={`text-xs ${levelInfo.textColor}`}>
            {levelInfo.description}
          </p>
        </div>

        {/* Current Status Overview */}
        <div className="grid grid-cols-2 gap-4 p-3 bg-navy rounded-lg border border-tan-dark">
          <div className="text-center">
            <div className="text-tan font-bold text-lg">{user.bro_score || 0}</div>
            <div className="text-tan-dark text-xs">Current BroScore</div>
          </div>
          <div className="text-center">
            <div className="text-tan font-bold text-lg">
              {access.verificationLevel === 'full' ? '4/4' : 
               access.verificationLevel === 'apply' ? '3/4' : 
               access.verificationLevel === 'browse' ? '1/4' : '0/4'}
            </div>
            <div className="text-tan-dark text-xs">Requirements Met</div>
          </div>
        </div>

        {/* Individual Verification Steps */}
        <div className="space-y-3">
          <h4 className="text-tan font-medium text-sm">Verification Requirements:</h4>
          
          {/* BroCode Verification */}
          <div className="flex items-center justify-between p-3 bg-navy rounded-lg border border-tan-dark">
            <div className="flex items-center space-x-3">
              {getStepIcon('brocode', access.hasValidBroCode)}
              <div>
                <div className="text-tan text-sm font-medium">BroCode Connected</div>
                <div className="text-tan-dark text-xs">
                  {access.hasValidBroCode ? 
                    `Connected: ${user.brocode}` : 
                    'Connect your BroCode to get started'
                  }
                </div>
              </div>
            </div>
            {access.hasValidBroCode ? (
              <CheckCircle className="text-green-400" size={16} />
            ) : (
              <AlertCircle className="text-orange-400" size={16} />
            )}
          </div>

          {/* Twitter Verification */}
          <div className="flex items-center justify-between p-3 bg-navy rounded-lg border border-tan-dark">
            <div className="flex items-center space-x-3">
              {getStepIcon('twitter', access.hasTwitterHandle)}
              <div>
                <div className="text-tan text-sm font-medium">Twitter Verified</div>
                <div className="text-tan-dark text-xs">
                  {access.hasTwitterHandle ? 
                    `Verified: @${user.twitter_handle}` : 
                    'Link and verify your Twitter account'
                  }
                </div>
              </div>
            </div>
            {access.hasTwitterHandle ? (
              <CheckCircle className="text-green-400" size={16} />
            ) : (
              <AlertCircle className="text-orange-400" size={16} />
            )}
          </div>

          {/* Telegram Verification */}
          <div className="flex items-center justify-between p-3 bg-navy rounded-lg border border-tan-dark">
            <div className="flex items-center space-x-3">
              {getStepIcon('telegram', access.isVerified)}
              <div>
                <div className="text-tan text-sm font-medium">Telegram Verified</div>
                <div className="text-tan-dark text-xs">
                  {access.isVerified ? 
                    'Telegram account verified' : 
                    'Complete Telegram verification process'
                  }
                </div>
              </div>
            </div>
            {access.isVerified ? (
              <CheckCircle className="text-green-400" size={16} />
            ) : (
              <AlertCircle className="text-orange-400" size={16} />
            )}
          </div>

          {/* BroScore Requirement */}
          <div className="flex items-center justify-between p-3 bg-navy rounded-lg border border-tan-dark">
            <div className="flex items-center space-x-3">
              {getStepIcon('broscore', access.hasMinimumScore)}
              <div>
                <div className="text-tan text-sm font-medium">Minimum BroScore</div>
                <div className="text-tan-dark text-xs">
                  {access.hasMinimumScore ? 
                    `Requirement met: ${user.bro_score}/25` : 
                    `Need ${25 - (user.bro_score || 0)} more BroScore (${user.bro_score || 0}/25)`
                  }
                </div>
              </div>
            </div>
            {access.hasMinimumScore ? (
              <CheckCircle className="text-green-400" size={16} />
            ) : (
              <AlertCircle className="text-orange-400" size={16} />
            )}
          </div>
        </div>

        {/* Access Level Capabilities */}
        <div className="bg-navy rounded-lg border border-tan-dark p-3">
          <h4 className="text-tan font-medium text-sm mb-2">Current Capabilities:</h4>
          <div className="space-y-1">
            <div className="flex items-center text-xs">
              <CheckCircle className="text-green-400 mr-2" size={12} />
              <span className="text-tan-dark">Browse job listings</span>
            </div>
            <div className="flex items-center text-xs">
              {access.canApplyToJobs ? (
                <CheckCircle className="text-green-400 mr-2" size={12} />
              ) : (
                <Circle className="text-tan-dark mr-2" size={12} />
              )}
              <span className={access.canApplyToJobs ? 'text-tan-dark' : 'text-tan-dark opacity-50'}>
                Apply to jobs
              </span>
            </div>
            <div className="flex items-center text-xs">
              {access.canPostJobs ? (
                <CheckCircle className="text-green-400 mr-2" size={12} />
              ) : (
                <Circle className="text-tan-dark mr-2" size={12} />
              )}
              <span className={access.canPostJobs ? 'text-tan-dark' : 'text-tan-dark opacity-50'}>
                Post job listings
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VerificationStatus;